import { autorun, makeAutoObservable } from "mobx";
import { StoreNameEnum } from "../../enum/storesEnum";
import type { apiTypes } from "../../services";
import { api } from "../../services";
import {
	AttributeShelf,
	LoaderShelf,
	PartialAnticipationShelf,
	TotalAnticipationShelf,
	InputShelf,
} from "../../shelves";
import { parseMoneyToDecimal } from "../../utils/formatters";
import type { AuthenticationStore } from "../Authentication";
import type { AnticipationStoreState } from "./interfaces";
import CryptoJS from "crypto-js";
import { env } from "../../config";
import type { TotalSimulationResponse } from "../../services/APIService/types";

export enum TabSelectedEnum {
	DEFAULT = "DEFAULT",
	PARTIAL = "PARTIAL",
	TOTAL = "TOTAL",
}

export class AnticipationStore {
	public authentication: AuthenticationStore;
	public loader = new LoaderShelf();
	public partialAnticipationShelf = new PartialAnticipationShelf();
	public totalAnticipationShelf = new TotalAnticipationShelf();
	public tabSelected = new AttributeShelf<TabSelectedEnum>(TabSelectedEnum.DEFAULT);
	public sellerId = new InputShelf("");
	public sellerIdSelected = new AttributeShelf<string>("");
	public offersSimulated = new AttributeShelf<TotalSimulationResponse[]>([]);

	constructor(authentication: AuthenticationStore) {
		this.authentication = authentication;
		makeAutoObservable(this);

		// Carrega o estado salvo do localStorage
		this.loadState();

		// Monitora mudanças e salva o estado automaticamente
		autorun(() => {
			this.saveState();
		});
	}

	public get selectYears(): number {
		const yearsAdvance = this.totalAnticipationShelf.yearsAdvance.value;
		const years = yearsAdvance === 0 ? 10 : yearsAdvance > 10 ? 10 : yearsAdvance;
		return years;
	}

	// retorna todas ofertas já simuladas
	public get getOffersSimulated() {
		return this.offersSimulated.value;
	}

	public getTotalSimulation = async () => {
		if (this.loader.isLoading) {
			return;
		}

		try {
			this.loader.start();

			const res = await api.getTotalSimulation(this.authentication.getToken() || "", this.selectYears);
			this.onSuccessFetch(res);
		} catch (error) {
			console.error(error);
			return;
		} finally {
			this.loader.end();
		}
	};

	public onSelectPartialValue = async (): Promise<void> => {
		try {
			this.loader.start();
			const value = parseMoneyToDecimal(this.partialAnticipationShelf.selectedFGTSAnticipationValue.value).toString();
			const partialSimulationResult = await api.getPartialSimulation(value, this.authentication.getToken() || "");
			this.partialAnticipationShelf.hasSelectedFGTSAnticipationValue.set(true);
			this.partialAnticipationShelf.partialSimulationData.set(partialSimulationResult);
		} catch (e) {
			window.console.error(e);
		} finally {
			this.loader.end();
		}
	};

	public onVerifyEligible = async (): Promise<boolean> => {
		try {
			this.loader.start();
			await api.verifyEligible(this.authentication.getToken() || "");
			return true;
		} catch (e) {
			window.console.error(e);
			return false;
		} finally {
			this.loader.end();
		}
	};

	public onEditPartialValue = (): void => {
		this.partialAnticipationShelf.hasSelectedFGTSAnticipationValue.set(false);
		this.partialAnticipationShelf.selectedFGTSAnticipationValue.set("");
	};

	public onSuccessFetch = (offer: apiTypes.TotalSimulationResponse): void => {
		this.partialAnticipationShelf.setMaxFGTSValueAvailable(offer.disbursedIssueAmount, this.selectYears);
		this.totalAnticipationShelf.disbursedIssueAmount.set(offer.disbursedIssueAmount);
		this.totalAnticipationShelf.maxFGTSValueAvailable.set(offer.totalAmount);
		this.totalAnticipationShelf.blockedValue.set(offer.totalAmount);
		this.totalAnticipationShelf.contractFees.set(offer.totalPreFixedAmount);
		this.totalAnticipationShelf.IOF.set(offer.iofAmount);
		this.totalAnticipationShelf.CET.set(offer.annualCet);
		this.totalAnticipationShelf.yearsFees.set(offer.annualRate);
		this.totalAnticipationShelf.monthFees.set(offer.monthlyRate);
		this.totalAnticipationShelf.yearsAdvance.set(offer.yearsInAdvance);
		this.totalAnticipationShelf.numberOfInstallments.set(offer.numberOfInstallments);
		this.totalAnticipationShelf.installments.set(offer.installments);
		this.totalAnticipationShelf.refSelectYears.set(
			this.totalAnticipationShelf.refSelectYears.value === 0
				? offer.yearsInAdvance
				: this.totalAnticipationShelf.refSelectYears.value,
		);
		this.totalAnticipationShelf.refMaxFGTSValueAvailable.set(
			this.totalAnticipationShelf.refMaxFGTSValueAvailable.value === 0
				? offer.disbursedIssueAmount
				: this.totalAnticipationShelf.refMaxFGTSValueAvailable.value,
		);
	};

	// altera o valor da tab selecionada
	public onSelectTab = (tab: TabSelectedEnum): void => {
		this.tabSelected.set(tab);
	};

	// retorna valor da tab selecionada
	public getTabSelected = (): TabSelectedEnum => this.tabSelected.value;

	// Limpa o estado atual
	public clearState(): void {
		sessionStorage.removeItem(StoreNameEnum.ANTICIPATION);
	}

	// Reinicializa o estado da Anticipation
	public resetState = (): void => {
		this.resetStateAnticipation();
		this.clearState();
	};

	// altera o valor do sellerId após confirmação
	public saveSellerIdSelected = (value: string): void => {
		this.sellerIdSelected.set(value);
	};

	public saveOffersSimulated = (offers: TotalSimulationResponse[]) => {
		this.offersSimulated.set(offers);
	};

	private resetStateAnticipation = (): void => {
		this.tabSelected.set(TabSelectedEnum.DEFAULT);
		this.partialAnticipationShelf.setMaxFGTSValueAvailable(0, 10);
		this.totalAnticipationShelf.disbursedIssueAmount.set(0);
		this.totalAnticipationShelf.maxFGTSValueAvailable.set(0);
		this.totalAnticipationShelf.blockedValue.set(0);
		this.totalAnticipationShelf.contractFees.set(0);
		this.totalAnticipationShelf.IOF.set(0);
		this.totalAnticipationShelf.CET.set(0);
		this.totalAnticipationShelf.yearsFees.set(0);
		this.totalAnticipationShelf.monthFees.set(0);
		this.totalAnticipationShelf.yearsAdvance.set(0);
		this.totalAnticipationShelf.numberOfInstallments.set(0);
		this.totalAnticipationShelf.installments.set([]);
		this.totalAnticipationShelf.refSelectYears.set(0);
		this.totalAnticipationShelf.refMaxFGTSValueAvailable.set(0);
		this.sellerId.field.set("");
		this.sellerIdSelected.set("");
		this.offersSimulated.set([]);
	};

	// Salva o estado atual no sessionStorage
	private saveState(): void {
		const state = {
			tabSelected: this.tabSelected.value,
			selectYears: this.selectYears,
			selectedFGTSAnticipationValue: this.partialAnticipationShelf.selectedFGTSAnticipationValue.value,
			hasSelectedFGTSAnticipationValue: this.partialAnticipationShelf.hasSelectedFGTSAnticipationValue.value,
			partialSimulationData: this.partialAnticipationShelf.partialSimulationData.value,
			disbursedIssueAmount: this.totalAnticipationShelf.disbursedIssueAmount.value,
			maxFGTSValueAvailable: this.partialAnticipationShelf.maxFGTSValueAvailable.value,
			blockedValue: this.totalAnticipationShelf.blockedValue.value,
			contractFees: this.totalAnticipationShelf.contractFees.value,
			IOF: this.totalAnticipationShelf.IOF.value,
			CET: this.totalAnticipationShelf.CET.value,
			yearsFees: this.totalAnticipationShelf.yearsFees.value,
			monthFees: this.totalAnticipationShelf.monthFees.value,
			yearsAdvance: this.totalAnticipationShelf.yearsAdvance.value,
			numberOfInstallments: this.totalAnticipationShelf.numberOfInstallments.value,
			installments: this.totalAnticipationShelf.installments.value,
			refSelectYears: this.totalAnticipationShelf.refSelectYears.value,
			refMaxFGTSValueAvailable: this.totalAnticipationShelf.refMaxFGTSValueAvailable.value,
			sellerId: this.sellerId.field.value,
			sellerIdSelected: this.sellerIdSelected.value,
			offersSimulated: this.offersSimulated.value,
		};

		const cipherInfoAnticipation: string = CryptoJS.AES.encrypt(JSON.stringify(state), env.cryptoKey).toString();
		sessionStorage.setItem(StoreNameEnum.ANTICIPATION, cipherInfoAnticipation);
	}

	// Carrega o estado salvo do sessionStorage
	private loadState(): void {
		const savedState = sessionStorage.getItem(StoreNameEnum.ANTICIPATION);
		if (savedState) {
			const bytes = CryptoJS.AES.decrypt(savedState, env.cryptoKey);
			const decryptedAnticipation: string = bytes.toString(CryptoJS.enc.Utf8);
			const state: AnticipationStoreState = JSON.parse(decryptedAnticipation);
			this.tabSelected.set(state.tabSelected);
			this.partialAnticipationShelf.selectedFGTSAnticipationValue.set(state.selectedFGTSAnticipationValue);
			this.partialAnticipationShelf.hasSelectedFGTSAnticipationValue.set(state.hasSelectedFGTSAnticipationValue);
			this.partialAnticipationShelf.partialSimulationData.set(state.partialSimulationData);
			this.totalAnticipationShelf.disbursedIssueAmount.set(state.disbursedIssueAmount);
			this.partialAnticipationShelf.setMaxFGTSValueAvailable(state.maxFGTSValueAvailable, state.selectYears);
			this.totalAnticipationShelf.maxFGTSValueAvailable.set(state.maxFGTSValueAvailable);
			this.totalAnticipationShelf.blockedValue.set(state.blockedValue);
			this.totalAnticipationShelf.contractFees.set(state.contractFees);
			this.totalAnticipationShelf.IOF.set(state.IOF);
			this.totalAnticipationShelf.CET.set(state.CET);
			this.totalAnticipationShelf.yearsFees.set(state.yearsFees);
			this.totalAnticipationShelf.monthFees.set(state.monthFees);
			this.totalAnticipationShelf.yearsAdvance.set(state.yearsAdvance);
			this.totalAnticipationShelf.numberOfInstallments.set(state.numberOfInstallments);
			this.totalAnticipationShelf.installments.set(state.installments);
			this.totalAnticipationShelf.refSelectYears.set(state.refSelectYears);
			this.totalAnticipationShelf.refMaxFGTSValueAvailable.set(state.refMaxFGTSValueAvailable);
			this.sellerId.field.set(state.sellerId);
			this.sellerIdSelected.set(state.sellerIdSelected);
			this.offersSimulated.set(state.offersSimulated);
		}
	}
}
